// Core packages

// Third party packages
import moment from "moment";

// Custom packages
import { labelOf } from "src/app/modules/properties/document-archive-insert.utils";
import {
  archiveExecutorName,
  archiveGroupLabel,
  ArchiveSupplierNames,
} from "src/app/modules/properties/document-archive-row-actions.utils";
import {
  DocumentArchiveTreeArea,
  DocumentArchiveTreeContent,
  DocumentArchiveTreeDoc,
  DocumentArchiveTreeGroup,
  DocumentArchiveTreeRow,
} from "src/app/shared/interfaces/document-archive-doc.interface";

/**
 * Pure helpers of the search within the documents of ONE property on the 3.0.0 archive
 * (`/properties/:propertyId/searchV3`): which rows of the archive tree a search finds, and how a
 * table of results is sorted.
 *
 * There is no back-end route for this search: the page reads the same tree the archive page reads
 * (`GET /document-archive-docs/tree`) and filters it here, as the legacy search page filtered the
 * legacy archive. The rules are the legacy ones (`search-documents.page.component.ts`), with two
 * of its bugs fixed: the text typed was used as a regular expression without escaping it, so a
 * parenthesis broke the search, and the expression carried the `g` flag while being reused, so
 * `test` resumed from the previous match and skipped rows that matched.
 *
 * @since 3.0.0
 */

/** Shortest text the "Cerca" field searches for: shorter, it is ignored, as in the legacy */
export const ARCHIVE_SEARCH_MIN_TEXT_LENGTH = 3;

/** A word the users type, and the word the documents are actually titled with */
export interface ArchiveSearchSynonym {
  /** What the user types, lower case, compared with the WHOLE text typed */
  key: string;
  /** What is searched instead */
  value: string;
}

/**
 * The synonyms of the legacy search page, copied as they are, in the same order
 * (`docSearchChanged` in `search-documents.page.component.ts`).
 *
 * A synonym replaces the WHOLE text, and only when the whole text is its key: "bilancio" searches
 * "rendiconto", "bilancio 2023" searches "bilancio 2023". The first entry wins, so the second
 * "81/08" (towards "dvr") never applies, exactly as in the legacy.
 */
export const ARCHIVE_SEARCH_SYNONYMS: ArchiveSearchSynonym[] = [
  { key: "bilancio", value: "rendiconto" },
  { key: "bilanci", value: "rendiconto" },
  { key: "consuntivo", value: "rendiconto" },
  { key: "consuntivi", value: "rendiconto" },
  { key: "dipendenti", value: "dipendente" },
  { key: "custode", value: "dipendente" },
  { key: "custodi", value: "dipendente" },
  { key: "portiere", value: "dipendente" },
  { key: "portieri", value: "dipendente" },
  { key: "portinaio", value: "dipendente" },
  { key: "portinai", value: "dipendente" },
  { key: "guardiola", value: "portineria" },
  { key: "guardiole", value: "portineria" },
  { key: "responsabile servizio prevenzione e protezione", value: "rspp" },
  { key: "responsabili servizio prevenzione e protezione", value: "rspp" },
  { key: "responsabili servizi prevenzione e protezione", value: "rspp" },
  { key: "sicurezza dipendente", value: "rspp" },
  { key: "sicurezza dipendenti", value: "rspp" },
  { key: "81/08", value: "rspp" },
  { key: "documento valutazione rischi", value: "dvr" },
  { key: "documento valutazioni rischi", value: "dvr" },
  { key: "documenti valutazione rischi", value: "dvr" },
  { key: "documenti valutazioni rischi", value: "dvr" },
  { key: "sicurezza condominio", value: "dvr" },
  { key: "sicurezza condomini", value: "dvr" },
  { key: "rischi", value: "dvr" },
  { key: "rischio", value: "dvr" },
  { key: "626", value: "dvr" },
  { key: "81/08", value: "dvr" },
  { key: "licenza di occupazione", value: "abitabilita'/agibilita'" },
  { key: "licenze di occupazione", value: "abitabilita'/agibilita'" },
  { key: "sanatoria", value: "condono" },
  { key: "sanatorie", value: "condono" },
  { key: "contatore luce", value: "contatore elettrico" },
  { key: "contatori luce", value: "contatore elettrico" },
  { key: "luci", value: "contatore elettrico" },
  { key: "luce", value: "contatore elettrico" },
  { key: "illuminazione", value: "contatore elettrico" },
  { key: "illuminazioni", value: "contatore elettrico" },
  { key: "fornitura energia elettrica", value: "contatore elettrico" },
  { key: "forniture energia elettrica", value: "contatore elettrico" },
  { key: "impianto di m.a.t.", value: "messa a terra" },
  { key: "impianti di m.a.t.", value: "messa a terra" },
  { key: "cabina di media, media tensione", value: "mt" },
  { key: "cabine di media, media tensione", value: "mt" },
  { key: "pannelli solari", value: "fotovoltaico" },
  { key: "pannello solare", value: "fotovoltaico" },
  { key: "latrici solari", value: "fotovoltaico" },
  { key: "latrice solare", value: "fotovoltaico" },
  { key: "generatore", value: "gruppo elettrogeno" },
  { key: "generatori", value: "gruppo elettrogeno" },
  { key: "alimentatore", value: "gruppo elettrogeno" },
  { key: "alimentatori", value: "gruppo elettrogeno" },
  { key: "parafulmine", value: "scariche atmosferiche" },
  { key: "parafulmini", value: "scariche atmosferiche" },
  { key: "protezione fulmini", value: "scariche atmosferiche" },
  { key: "protezione fulmine", value: "scariche atmosferiche" },
  { key: "protezioni fulmini", value: "scariche atmosferiche" },
  { key: "protezioni fulmine", value: "scariche atmosferiche" },
  { key: "caldaia", value: "centrale termica" },
  { key: "caldaie", value: "centrale termica" },
  { key: "riscaldamento", value: "centrale termica" },
  { key: "riscaldamenti", value: "centrale termica" },
  { key: "bruciatore", value: "centrale termica" },
  { key: "bruciatori", value: "centrale termica" },
  { key: "metano", value: "contatore gas" },
  { key: "rete gas", value: "contatore gas" },
  { key: "reti gas", value: "contatore gas" },
  { key: "camino", value: "canna fumaria" },
  { key: "camini", value: "canna fumaria" },
  { key: "climatizzazione", value: "condizionamento" },
  { key: "climatizzazioni", value: "condizionamento" },
  { key: "cdz", value: "condizionamento" },
  { key: "aria condizionata", value: "condizionamento" },
  { key: "impianto di raffreddamento", value: "condizionamento" },
  { key: "impianti di raffreddamento", value: "condizionamento" },
  { key: "impianto elevatore", value: "ascensore" },
  { key: "impianti elevatore", value: "ascensore" },
  { key: "autoclave", value: "centrale idrica" },
  { key: "pompe acqua", value: "impianto di sollevamento acque" },
  { key: "pompa acqua", value: "impianto di sollevamento acque" },
  { key: "videosorveglianza", value: "tvcc" },
  { key: "videosorveglianze", value: "tvcc" },
  { key: "telecamere", value: "tvcc" },
  { key: "telecamera", value: "tvcc" },
  { key: "passo carraio", value: "automazione cancelli" },
  { key: "passi carrai", value: "automazione cancelli" },
  { key: "cancello elettrico", value: "automazione cancelli" },
  { key: "cancelli elettrici", value: "automazione cancelli" },
  { key: "allarme", value: "antintrusione" },
  { key: "allarmi", value: "antintrusione" },
  { key: "antifurto", value: "antintrusione" },
  { key: "antifurti", value: "antintrusione" },
  { key: "scia", value: "prevenzione incendi" },
  { key: "cpi", value: "prevenzione incendi" },
  { key: "certificato prevenzione incendi", value: "prevenzione incendi" },
  { key: "certificati prevenzione incendi", value: "prevenzione incendi" },
  { key: "vigili del fuoco", value: "prevenzione incendi" },
  { key: "vigile del fuoco", value: "prevenzione incendi" },
  { key: "818/84", value: "prevenzione incendi" },
  { key: "manichette", value: "rete idranti" },
  { key: "manichetta", value: "rete idranti" },
  { key: "attestato certificazione energetica", value: "ace/ape" },
  { key: "attestati certificazione energetica", value: "ace/ape" },
  { key: "attestati certificazioni energetiche", value: "ace/ape" },
  { key: "ape", value: "ace/ape" },
  { key: "attestato di prestazione energetica", value: "ace/ape" },
  { key: "attestati di prestazioni energetiche", value: "ace/ape" },
  { key: "certificazione energetica", value: "ace/ape" },
  { key: "certificazioni energetiche", value: "ace/ape" },
  { key: "spurghi", value: "rete fognaria" },
  { key: "spurgo", value: "rete fognaria" },
  { key: "fogna", value: "rete fognaria" },
  { key: "fogne", value: "rete fognaria" },
  { key: "fognatura", value: "rete fognaria" },
  { key: "fognature", value: "rete fognaria" },
  { key: "disegni", value: "elaborati grafici" },
  { key: "disegno", value: "elaborati grafici" },
  { key: "tavola", value: "elaborati grafici" },
  { key: "tavole", value: "elaborati grafici" },
  { key: "planimetria", value: "elaborati grafici" },
  { key: "planimetrie", value: "elaborati grafici" },
  { key: "piantina", value: "elaborati grafici" },
  { key: "piantine", value: "elaborati grafici" },
];

/**
 * What the user asked for, read off the search form.
 *
 * @since 3.0.0
 */
export interface ArchiveSearchFilters {
  /** Text of the "Cerca" field, as typed */
  text: string;
  /** Supplier chosen in the "Fornitore" field: an id of the SUPPLIERS back-end, `""` for none */
  supplierId: string;
  /** First day of the document date, `YYYY-MM-DD`, `null` for none */
  dateFrom: string | null;
  /** Last day of the document date, `YYYY-MM-DD`, included, `null` for none */
  dateTo: string | null;
  /** Text of the "Protocollo" field, as typed */
  protocol: string;
  /** Text of the "Parola chiave" field, as typed */
  keyword: string;
}

/**
 * A row found, with the document type it is filed under: the type gives the title of the row and
 * is what the dialogs of its menu need.
 *
 * @since 3.0.0
 */
export interface ArchiveSearchRow {
  row: DocumentArchiveTreeRow;
  templateDoc: DocumentArchiveTreeDoc;
}

/**
 * One table of results: the rows found in a block of the archive.
 *
 * @since 3.0.0
 */
export interface ArchiveSearchBlock {
  /** The block, as the tree serves it: heading and number are read from it */
  group: DocumentArchiveTreeGroup;
  /** The rows found, in the order of the archive */
  rows: ArchiveSearchRow[];
  /** The same rows in the order the user sorted the table by; `rows` itself when unsorted */
  displayRows: ArchiveSearchRow[];
}

/**
 * The tables of results of a section.
 *
 * @since 3.0.0
 */
export interface ArchiveSearchSection {
  content: DocumentArchiveTreeContent;
  blocks: ArchiveSearchBlock[];
}

/**
 * The sections of results of an area.
 *
 * @since 3.0.0
 */
export interface ArchiveSearchArea {
  area: DocumentArchiveTreeArea;
  sections: ArchiveSearchSection[];
}

/**
 * The columns a table of documents can be sorted by: the ones of the search, and "Codice", which
 * only the tables of the dossier print.
 */
export type ArchiveSearchSortKey = "type" | "description" | "supplier" | "date" | "expiry" | "code";

/** @see ArchiveSearchSortKey */
const SORT_KEYS: string[] = ["type", "description", "supplier", "date", "expiry", "code"];

/**
 * The letters a word is turned from singular to plural on, and back: the last one is swapped for
 * any of them, so "contratto" also finds "contratti". The legacy rule, on unaccented lower case
 * vowels only.
 */
const SWAPPED_VOWELS = "aeiou";

/** Characters a regular expression reads as syntax: the same list the back-end search escapes */
const REGEXP_SYNTAX = /[.*+?^${}()|[\]\\]/g;

/** A calendar day and nothing else */
const DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * A date with a time of day: group 1 is the date, group 2 the zone when there is one.
 *
 * With a zone the value is an instant, and its day depends on where it is read; without one it is
 * a wall clock date.
 */
const DATE_TIME_PATTERN = /^(\d{4}-\d{2}-\d{2})T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(Z|[+-]\d{2}(?::?\d{2})?)?$/i;

/**
 * The time zone a document date is a day in: the one of the general search of the back-end, so the
 * two searches put a document on the same day.
 */
const SEARCH_TIME_ZONE = "Europe/Rome";

/**
 * Formatter of the Rome calendar day, built on first use.
 *
 * `undefined` until then, `null` when the runtime has no time zone database and refused to build it.
 */
let romeDayFormatter: Intl.DateTimeFormat | null | undefined;

/**
 * `text` as a pattern that matches it literally.
 *
 * What the user types is never syntax: escaped, a parenthesis is a parenthesis to look for instead
 * of an error, and no input can make the match backtrack.
 *
 * @since 3.0.0
 *
 * @param text The text typed
 */
export function escapeArchiveSearchText(text: string): string {
  return typeof text === "string" ? text.replace(REGEXP_SYNTAX, "\\$&") : "";
}

/**
 * The pattern the "Cerca" field searches for, or null when the text is too short to search.
 *
 * The legacy rules:
 *  1. the text is trimmed and must be at least ARCHIVE_SEARCH_MIN_TEXT_LENGTH characters long;
 *  2. a text that is a synonym is replaced by the word it stands for (@see ARCHIVE_SEARCH_SYNONYMS);
 *  3. a word ending in a vowel matches with any vowel in that place: singular and plural.
 *
 * Two departures from the legacy, both fixes:
 *  - the text is ESCAPED, and the pattern has no `g` flag: @see escapeArchiveSearchText;
 *  - the length is checked on what the user TYPED, before the synonym: the legacy checked the
 *    replacement, so "cabina di media, media tensione", whose synonym is "mt", searched nothing.
 *
 * Case is ignored everywhere, the final vowel included: the legacy expanded "contratto" and not
 * "CONTRATTO".
 *
 * @since 3.0.0
 *
 * @param text The text typed in "Cerca"
 */
export function archiveSearchTextPattern(text: unknown): RegExp | null {
  if (typeof text !== "string") {
    return null;
  }
  const typed = text.trim().toLowerCase();
  if (typed.length < ARCHIVE_SEARCH_MIN_TEXT_LENGTH) {
    return null;
  }

  let wanted = typed;
  for (let i = 0; i < ARCHIVE_SEARCH_SYNONYMS.length; i++) {
    if (ARCHIVE_SEARCH_SYNONYMS[i].key.toLowerCase() === typed) {
      wanted = ARCHIVE_SEARCH_SYNONYMS[i].value.toLowerCase();
      break;
    }
  }

  const last = wanted.charAt(wanted.length - 1);
  const source =
    last !== "" && SWAPPED_VOWELS.indexOf(last) > -1
      ? `${escapeArchiveSearchText(wanted.substring(0, wanted.length - 1))}[${SWAPPED_VOWELS}]`
      : escapeArchiveSearchText(wanted);
  // No `g` flag: the same pattern is tested on every row, and a global one would carry on from
  // where the previous test stopped
  return new RegExp(source, "i");
}

/**
 * Whether `day` (`YYYY-MM-DD`) exists on the calendar: no 30 February, no month 13.
 *
 * @param day The day
 */
function isCalendarDay(day: string): boolean {
  return moment(day, "YYYY-MM-DD", true).isValid();
}

/**
 * The formatter of the Rome calendar day, or null when the runtime cannot build one.
 *
 * Built lazily rather than when the file is loaded: the file is part of the bundle every page loads,
 * and a refusal at load time would take the whole application down with it.
 */
function romeDayFormat(): Intl.DateTimeFormat | null {
  if (romeDayFormatter === undefined) {
    try {
      romeDayFormatter = new Intl.DateTimeFormat("en-US", {
        timeZone: SEARCH_TIME_ZONE,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    } catch (err) {
      // RangeError from a runtime without the time zone database. The local calendar is the
      // closest answer left, and it is the one the date column prints with anyway
      romeDayFormatter = null;
    }
  }
  return romeDayFormatter;
}

/**
 * The Rome calendar day an instant falls on, `YYYY-MM-DD`.
 *
 * @param instant The instant
 */
function romeDayOf(instant: Date): string | null {
  if (isNaN(instant.getTime())) {
    return null;
  }
  const formatter = romeDayFormat();
  if (!formatter) {
    return moment(instant).format("YYYY-MM-DD");
  }

  let year = "";
  let month = "";
  let day = "";
  formatter.formatToParts(instant).forEach((part) => {
    if (part.type === "year") {
      year = part.value;
    } else if (part.type === "month") {
      month = part.value;
    } else if (part.type === "day") {
      day = part.value;
    }
  });
  return year.length === 4 && month.length === 2 && day.length === 2
    ? `${year}-${month}-${day}`
    : null;
}

/**
 * The day a document is dated, `YYYY-MM-DD`, or null when its date cannot be put on a calendar.
 *
 * The rule of the general search of the back-end, so that both searches find a document on the same
 * day:
 *  - an instant (a date stored as a date, or an ISO text with its zone) is the day it falls on in
 *    Rome. `2024-03-14T23:00:00.000Z`, the Rome midnight of 15 March as some writers stored it, is
 *    15 March, the day the date column prints;
 *  - a bare day (`2024-03-15`), or a date and time with no zone, is the date it spells;
 *  - anything else is no date: `15/03/2024`, `NON PRESENTE`, an empty value. Those rows are never
 *    found by a date filter, as on the back-end.
 *
 * @since 3.0.0
 *
 * @param value `data.dataDocumento` of a row, or `dateExpired`
 */
export function archiveDocumentDay(value: unknown): string | null {
  if (value instanceof Date) {
    return romeDayOf(value);
  }
  if (typeof value !== "string") {
    return null;
  }

  const text = value.trim();
  if (DAY_PATTERN.test(text)) {
    return isCalendarDay(text) ? text : null;
  }

  const dateTime = DATE_TIME_PATTERN.exec(text);
  if (!dateTime || !isCalendarDay(dateTime[1])) {
    return null;
  }
  return dateTime[2] ? romeDayOf(new Date(text)) : dateTime[1];
}

/**
 * The day a date field of the search form stands for, `YYYY-MM-DD`, or null when it is empty.
 *
 * The datepicker of the application works in UTC (`MAT_MOMENT_DATE_ADAPTER_OPTIONS.useUtc`), so the
 * day picked is a moment at midnight UTC of that very day, and its own calendar date is the answer:
 * read in Rome it would still be that day, read in a zone west of UTC it would be the day before.
 *
 * @since 3.0.0
 *
 * @param value The value of the field: a moment, a Date or a text
 */
export function archiveSearchDay(value: unknown): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (moment.isMoment(value)) {
    return value.isValid() ? value.format("YYYY-MM-DD") : null;
  }
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : moment(value).format("YYYY-MM-DD");
  }
  return archiveDocumentDay(value);
}

/**
 * The filters of the search form, from its value.
 *
 * The keys are the ones of the form of the page: `docSearch`, `supplier`, `dateFrom`, `dateTo`,
 * `protocol`, `keyword` — the names of the legacy search page.
 *
 * @since 3.0.0
 *
 * @param value The value of the search form
 */
export function archiveSearchFiltersOf(value: unknown): ArchiveSearchFilters {
  const source: { [key: string]: unknown } =
    value && typeof value === "object" ? (value as { [key: string]: unknown }) : {};
  return {
    text: typeof source.docSearch === "string" ? source.docSearch : "",
    supplierId: typeof source.supplier === "string" ? source.supplier.trim() : "",
    dateFrom: archiveSearchDay(source.dateFrom),
    dateTo: archiveSearchDay(source.dateTo),
    protocol: typeof source.protocol === "string" ? source.protocol : "",
    keyword: typeof source.keyword === "string" ? source.keyword : "",
  };
}

/**
 * The pattern of a free text filter ("Protocollo", "Parola chiave"): what was typed, trimmed and
 * escaped, anywhere in the value, case ignored. Null when nothing was typed.
 *
 * @param text The text typed
 */
function freeTextPattern(text: unknown): RegExp | null {
  const needle = typeof text === "string" ? text.trim() : "";
  return needle ? new RegExp(escapeArchiveSearchText(needle), "i") : null;
}

/**
 * Whether the user asked for anything at all.
 *
 * As in the legacy, a search with no filter finds nothing: the page asks for a filter instead of
 * listing the whole archive, which is what the archive page is for. A text shorter than
 * ARCHIVE_SEARCH_MIN_TEXT_LENGTH is no filter.
 *
 * @since 3.0.0
 *
 * @param filters The filters of the form
 */
export function hasArchiveSearchFilter(filters: ArchiveSearchFilters | null | undefined): boolean {
  if (!filters) {
    return false;
  }
  return !!(
    archiveSearchTextPattern(filters.text) ||
    filters.supplierId ||
    filters.dateFrom ||
    filters.dateTo ||
    freeTextPattern(filters.protocol) ||
    freeTextPattern(filters.keyword)
  );
}

/**
 * Whether a stored value holds the text of a free text filter.
 *
 * A text, a number (the legacy matched them too, as text) or a list of texts, any of which may hold
 * it. Anything else holds nothing.
 *
 * @param value   The stored value
 * @param pattern The filter
 */
function holdsText(value: unknown, pattern: RegExp): boolean {
  if (typeof value === "string") {
    return pattern.test(value);
  }
  if (typeof value === "number") {
    return pattern.test(String(value));
  }
  if (Array.isArray(value)) {
    return value.some((item: unknown) => typeof item === "string" && pattern.test(item));
  }
  return false;
}

/** The filters of a search, compiled once for every row */
interface CompiledArchiveSearch {
  text: RegExp | null;
  supplierId: string;
  dateFrom: string | null;
  dateTo: string | null;
  protocol: RegExp | null;
  keyword: RegExp | null;
}

/**
 * Whether a row is found by a search.
 *
 * Only the documents actually filed (`present`, the back-end rule) — the legacy also listed rows of
 * unfilled obligations holding some data, which had no action to offer. Never a row the cascade
 * deleted with its mapping element, never a mockup, and never the copy the contracts table borrows
 * from an element that has been switched off (`structureActive === false`): the legacy search kept
 * the documents of active structures only, and the original of that copy sits in the block of the
 * switched off element, which the search leaves out as well.
 *
 * Every filter given must hold, as in the legacy:
 *  - "Cerca": the labels of the area, the section, the block and the document type, or the
 *    description of the document (`data.contenutoDocumento`);
 *  - "Fornitore": `data.ragioneSocialeFornitoreId` is the supplier chosen;
 *  - "Protocollo", "Parola chiave": the text anywhere in `data.protocollo`, `data.keyword`;
 *  - "Dal", "Al": the day of `data.dataDocumento` (@see archiveDocumentDay), both ends included. A
 *    document whose date is marked "not applicable" (`data.dataDocumentochkNA`) is out as soon as a
 *    date is asked for, whatever date the field still holds, as on the back-end.
 *
 * @param row    The row
 * @param labels The labels the text is searched in
 * @param search The search
 */
function isRowFound(
  row: DocumentArchiveTreeRow | null | undefined,
  labels: string[],
  search: CompiledArchiveSearch
): boolean {
  if (!row || row.present !== true || row.deleted === true || row.mockup === true) {
    return false;
  }
  // `=== false`: the field is absent on every row outside the contracts table, and absent is not
  // switched off
  if (row.structureActive === false) {
    return false;
  }
  const data = row.data && typeof row.data === "object" ? row.data : null;
  if (!data) {
    return false;
  }

  if (search.text) {
    const pattern = search.text;
    const description = data.contenutoDocumento;
    const found =
      labels.some((label) => !!label && pattern.test(label)) ||
      ((typeof description === "string" || typeof description === "number") &&
        pattern.test(String(description)));
    if (!found) {
      return false;
    }
  }

  if (search.supplierId) {
    const supplierId = data.ragioneSocialeFornitoreId;
    if (
      (typeof supplierId !== "string" && typeof supplierId !== "number") ||
      String(supplierId) !== search.supplierId
    ) {
      return false;
    }
  }

  if (search.protocol && !holdsText(data.protocollo, search.protocol)) {
    return false;
  }
  if (search.keyword && !holdsText(data.keyword, search.keyword)) {
    return false;
  }

  if (search.dateFrom || search.dateTo) {
    if (data.dataDocumentochkNA === true) {
      return false;
    }
    const day = archiveDocumentDay(data.dataDocumento);
    if (!day) {
      return false;
    }
    if (search.dateFrom && day < search.dateFrom) {
      return false;
    }
    if (search.dateTo && day > search.dateTo) {
      return false;
    }
  }

  return true;
}

/**
 * The rows of the archive tree a search finds, grouped as the archive is: area, section, block.
 *
 * Walks the tree in its own order and keeps it: the back-end sends it ordered, and the results read
 * like the archive with the rows that do not match taken out. A block, a section or an area with no
 * row found is left out.
 *
 * What the archive page hides by default is out of the search too: the blocks of a disabled or
 * removed mapping element (`group.active === false`) and the sections out of service
 * (`content.active === false`), with everything under them. So are the copies of their contracts in
 * the contracts table (@see isRowFound), as in the legacy search.
 *
 * The aggregated contracts section is searched like any other, as in the legacy: a contract found
 * by its own words (its type, its description) is listed twice, under its own section and in that
 * table, where the name of its element is printed beside the type. Found by the title of its own
 * section or block only, it is listed once: the contracts table has a title of its own.
 *
 * Nothing is found without a filter (@see hasArchiveSearchFilter).
 *
 * @since 3.0.0
 *
 * @param areas   The tree of the property
 * @param filters What the user asked for
 * @param lang    Language of the page: the labels are searched as the user reads them
 */
export function searchArchiveTree(
  areas: DocumentArchiveTreeArea[] | null | undefined,
  filters: ArchiveSearchFilters,
  lang: string
): ArchiveSearchArea[] {
  if (!hasArchiveSearchFilter(filters)) {
    return [];
  }

  const search: CompiledArchiveSearch = {
    text: archiveSearchTextPattern(filters.text),
    supplierId: filters.supplierId,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    protocol: freeTextPattern(filters.protocol),
    keyword: freeTextPattern(filters.keyword),
  };

  const results: ArchiveSearchArea[] = [];
  (Array.isArray(areas) ? areas : []).forEach((area) => {
    if (!area) {
      return;
    }
    const areaLabel = labelOf(area.translation, lang);
    const sections: ArchiveSearchSection[] = [];

    (Array.isArray(area.content) ? area.content : []).forEach((content) => {
      if (!content || content.active === false) {
        return;
      }
      const contentLabel = labelOf(content.translation, lang);
      const blocks: ArchiveSearchBlock[] = [];

      (Array.isArray(content.groups) ? content.groups : []).forEach((group) => {
        if (!group || group.active === false) {
          return;
        }
        const groupLabel = archiveGroupLabel(group, lang);
        const rows: ArchiveSearchRow[] = [];

        (Array.isArray(group.documents) ? group.documents : []).forEach((templateDoc) => {
          if (!templateDoc) {
            return;
          }
          const labels = [areaLabel, contentLabel, groupLabel, labelOf(templateDoc.translation, lang)];
          (Array.isArray(templateDoc.archiveDocs) ? templateDoc.archiveDocs : []).forEach((row) => {
            if (isRowFound(row, labels, search)) {
              rows.push({ row, templateDoc });
            }
          });
        });

        if (rows.length) {
          blocks.push({ group, rows, displayRows: rows });
        }
      });

      if (blocks.length) {
        sections.push({ content, blocks });
      }
    });

    if (sections.length) {
      results.push({ area, sections });
    }
  });

  return results;
}

/**
 * The value a row is sorted on in a column, as text.
 *
 * What the column prints, not what is stored: the executor is the name on screen, the dates are
 * calendar days. A value missing is the empty text, which sorts first going up and last going down,
 * as in the legacy.
 *
 * @param entry         The row
 * @param key           The column
 * @param lang          Language of the page
 * @param supplierNames The supplier names resolved so far
 */
function sortValueOf(
  entry: ArchiveSearchRow,
  key: string,
  lang: string,
  supplierNames: ArchiveSupplierNames
): string {
  const row = entry.row;
  const data = row && row.data && typeof row.data === "object" ? row.data : {};
  switch (key) {
    case "type":
      return labelOf(entry.templateDoc ? entry.templateDoc.translation : null, lang).toLowerCase();
    case "description":
      return typeof data.contenutoDocumento === "string" || typeof data.contenutoDocumento === "number"
        ? String(data.contenutoDocumento).toLowerCase()
        : "";
    case "supplier":
      return archiveExecutorName(row, supplierNames).toLowerCase();
    case "date": {
      const day = archiveDocumentDay(data.dataDocumento);
      return day ? day : "";
    }
    case "expiry": {
      const day = archiveDocumentDay(row ? row.dateExpired : undefined);
      return day ? day : "";
    }
    case "code":
      return row && typeof row.documentCode === "string" ? row.documentCode.toLowerCase() : "";
    default:
      return "";
  }
}

/**
 * The rows of a block of the archive as ONE list, in the order the tree serves them: entry by entry
 * (a document type once per band), and the rows of each entry in their own order. Each row comes
 * with the entry it is filed under, the very object of the tree: it gives the title of the row and
 * is what the dialogs of its menu need.
 *
 * What the dossier sorts a table on (@see sortArchiveSearchRows): there the rows of a block are one
 * table, whatever entry they belong to, as in the legacy dossier.
 *
 * @since 3.0.0
 *
 * @param group The block, as the tree serves it
 */
export function archiveBlockRows(
  group: DocumentArchiveTreeGroup | null | undefined
): ArchiveSearchRow[] {
  const rows: ArchiveSearchRow[] = [];
  (group && Array.isArray(group.documents) ? group.documents : []).forEach((templateDoc) => {
    if (!templateDoc) {
      return;
    }
    (Array.isArray(templateDoc.archiveDocs) ? templateDoc.archiveDocs : []).forEach((row) => {
      if (row) {
        rows.push({ row, templateDoc });
      }
    });
  });
  return rows;
}

/**
 * The rows of a table of results, or of a block of the dossier, in the order of a column.
 *
 * With no direction, or a column it does not know, it answers the rows ITSELF, in the order of the
 * archive: the third click on a header gives that order back. Rows with the same value keep the
 * order of the archive between them.
 *
 * @since 3.0.0
 *
 * @param rows          The rows of the table, in the order of the archive
 * @param active        The column (@see ArchiveSearchSortKey)
 * @param direction     "asc", "desc" or "" for none
 * @param lang          Language of the page
 * @param supplierNames The supplier names resolved so far, which the executor column prints
 */
export function sortArchiveSearchRows(
  rows: ArchiveSearchRow[],
  active: string,
  direction: string,
  lang: string,
  supplierNames: ArchiveSupplierNames
): ArchiveSearchRow[] {
  const list = Array.isArray(rows) ? rows : [];
  if ((direction !== "asc" && direction !== "desc") || SORT_KEYS.indexOf(active) === -1) {
    return list;
  }

  const factor = direction === "asc" ? 1 : -1;
  const keyed = list.map((entry, index) => ({
    entry,
    index,
    value: sortValueOf(entry, active, lang, supplierNames),
  }));
  keyed.sort((a, b) => {
    const order =
      a.value === b.value
        ? 0
        : a.value.localeCompare(b.value, lang === "en" ? "en" : "it", { numeric: true });
    return order !== 0 ? order * factor : a.index - b.index;
  });
  return keyed.map((item) => item.entry);
}
